import * as React from "react";
import { Button } from "antd";
import { DateInput } from "./components/DateInput";
import { CalendarBox } from "./components/CalendarBox";
import { PresetList } from "./components/PresetList";
import { TimezoneSelector } from "./components/TimezoneSelector";
import { Popover } from "./components/Popover";
import { PickerTooltip } from "./components/PickerTooltip";
import {
  parse,
  detectRange,
  formatDateToYMD,
  formatDateWithGranularity,
  extractTimeFromDate,
  combineDateAndTime,
  isAbsoluteDate,
  getSystemTimezone,
  resolveTimezone,
  formatTooltipWithTimezone,
  validateDateConstraints,
  validateRangeOrder,
  getDefaultTimeForField,
  DATE_ERRORS,
  parseTimeInput,
  countExpressions,
  applyExpression,
  resolveExpression,
  convertValueForTimezoneSwitch,
} from "./utils/parser";
import { today, dateToCalendarDateTime, calendarDateTimeToDate, truncateToGranularity } from "./utils/calendarDateTime";
import type { FieldType, GranularityType } from "./utils/parser";
import type { DateRangePickerProps, RangePreset } from "./types";
import "./DateRangePicker.css";

// =============================================================================
// DateRangePicker Component
// Date range selection with dual calendars, presets, and timezone support
// =============================================================================

const LOCAL_TIMEZONE = getSystemTimezone();

export const DateRangePicker = React.memo(function DateRangePicker({
  from: fromValue,
  to: toValue,
  setFrom: setFromValue,
  setTo: setToValue,
  timezoneValue,
  timezoneValueType,
  onTimezoneChange,
  timezoneSwitchBehavior = "keepDisplay",
  presets,
  granularity = "date",
  minDate,
  maxDate,
  required = false,
  allowClear = false,
  disabled = false,
  placeholder = ["From date", "To date"],
  immediatelyResolveExpressions = false,
  tooltipPosition = "top",
  className,
  style,
}: DateRangePickerProps) {
  // Resolve effective timezone
  const effectiveTimezone = React.useMemo(() => resolveTimezone(timezoneValue, timezoneValueType), [
    timezoneValueType,
    timezoneValue,
  ]);

  // ---------------------------------------------------------------------------
  // Resolve Props
  // ---------------------------------------------------------------------------

  const [fromAllowClear, toAllowClear] = Array.isArray(allowClear) ? allowClear : [allowClear, allowClear];
  const [fromDisabled, toDisabled] = Array.isArray(disabled) ? disabled : [disabled, disabled];
  const [fromRequired, toRequired] = Array.isArray(required) ? required : [required, required];
  const [fromPlaceholder, toPlaceholder] = placeholder;

  // Placeholders with (Required) suffix
  const resolvedFromPlaceholder = fromRequired ? `${fromPlaceholder} (Required)` : fromPlaceholder;
  const resolvedToPlaceholder = toRequired ? `${toPlaceholder} (Required)` : toPlaceholder;

  // ---------------------------------------------------------------------------
  // Resolve Min/Max Dates
  // ---------------------------------------------------------------------------

  const resolvedMinDate = React.useMemo(() => {
    if (!minDate) return undefined;
    const parsed = parse(minDate, { pickerType: "single", timezone: "UTC" });
    if (parsed.isValid && parsed.resolved) {
      return formatDateToYMD(parsed.resolved);
    }
    console.error(`[DateRangePicker] Invalid minDate: "${minDate}"`);
    return undefined;
  }, [minDate]);

  const resolvedMaxDate = React.useMemo(() => {
    if (!maxDate) return undefined;
    const parsed = parse(maxDate, { pickerType: "single", timezone: "UTC" });
    if (parsed.isValid && parsed.resolved) {
      return formatDateToYMD(parsed.resolved);
    }
    console.error(`[DateRangePicker] Invalid maxDate: "${maxDate}"`);
    return undefined;
  }, [maxDate]);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [isOpen, setIsOpen] = React.useState(false);
  const [activeField, setActiveField] = React.useState<"from" | "to">("from");
  const [fromInputText, setFromInputText] = React.useState("");
  const [toInputText, setToInputText] = React.useState("");
  const [fromErrorReason, setFromErrorReason] = React.useState<string | null>(null);
  const [toErrorReason, setToErrorReason] = React.useState<string | null>(null);
  const [hoveredDate, setHoveredDate] = React.useState<string | null>(null);
  // Hover tick to force re-render for dynamic expression tooltip evaluation
  const [, setHoverTick] = React.useState(0);

  // Click cycling state - track which field initiated open and cycle count
  const [initiatingField, setInitiatingField] = React.useState<"from" | "to">("from");
  const [clickCycle, setClickCycle] = React.useState(0);

  // Time values for granularity support - initialized empty, will be set from props
  const [fromTimeValue, setFromTimeValue] = React.useState("");
  const [toTimeValue, setToTimeValue] = React.useState("");

  // Time input error state
  const [fromTimeError, setFromTimeError] = React.useState(false);
  const [toTimeError, setToTimeError] = React.useState(false);

  // Previous valid time values for undo
  const [previousFromTimeValue, setPreviousFromTimeValue] = React.useState<string | null>(null);
  const [previousToTimeValue, setPreviousToTimeValue] = React.useState<string | null>(null);

  // Track whether the user manually changed the time input (vs auto-populated from expression resolution)
  const [fromTimeManuallySet, setFromTimeManuallySet] = React.useState(false);
  const [toTimeManuallySet, setToTimeManuallySet] = React.useState(false);

  // Calendar navigation - independent calendars using library
  const todayDate = today("UTC");
  const [leftYear, setLeftYear] = React.useState(() => todayDate.year);
  const [leftMonth, setLeftMonth] = React.useState(() => todayDate.month);
  const [rightYear, setRightYear] = React.useState(() => {
    // Next month
    return todayDate.month === 12 ? todayDate.year + 1 : todayDate.year;
  });
  const [rightMonth, setRightMonth] = React.useState(() => {
    // Next month
    return todayDate.month === 12 ? 1 : todayDate.month + 1;
  });

  // Previous values for undo
  const [previousFromValue, setPreviousFromValue] = React.useState<string | null | undefined>(undefined);
  const [previousToValue, setPreviousToValue] = React.useState<string | null | undefined>(undefined);

  const anchorRef = React.useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  // Format date for display based on granularity
  const formatForDisplay = React.useCallback((date: Date): string => formatDateWithGranularity(date, granularity), [
    granularity,
  ]);

  // Extract time string from Date based on granularity (for time input)
  const extractTime = React.useCallback(
    (date: Date): string => {
      if (granularity === "date") return "";
      return extractTimeFromDate(date, granularity as "hour" | "minute" | "second");
    },
    [granularity],
  );

  // Build ISO string from date and time
  const buildISOString = React.useCallback(
    (dateStr: string, timeStr: string): string => {
      if (granularity === "date") {
        return `${dateStr}T00:00:00Z`;
      }
      return combineDateAndTime(dateStr, timeStr);
    },
    [granularity],
  );

  // Get default time for from/to based on granularity (wrapper for parser utility)
  const getDefaultTime = React.useCallback(
    (fieldType: FieldType): string => getDefaultTimeForField(fieldType, granularity as GranularityType),
    [granularity],
  );

  // Get error message for min/max validation and range ordering
  const getDateRangeError = (dateStr: string, resolvedDate: Date, editingField: FieldType): string | null => {
    // Check min/max constraints (uses date string for date-level comparison)
    const constraintResult = validateDateConstraints(dateStr, resolvedMinDate, resolvedMaxDate);
    if (!constraintResult.isValid) {
      return constraintResult.error!;
    }

    // Check from/to ordering - use full timestamps for comparison (not just dates)
    const otherValue = editingField === "from" ? toValue : fromValue;
    if (otherValue) {
      const otherTimestamp = isAbsoluteDate(otherValue)
        ? new Date(otherValue).getTime()
        : (() => {
            const parsed = parse(otherValue, { pickerType: "single", timezone: effectiveTimezone });
            return parsed.resolved ? parsed.resolved.getTime() : null;
          })();

      if (otherTimestamp !== null) {
        const newTimestamp = resolvedDate.getTime();
        if (editingField === "from") {
          // Editing from field - check it's not after to (at second precision)
          if (newTimestamp > otherTimestamp) {
            return DATE_ERRORS.FROM_AFTER_TO;
          }
        } else {
          // Editing to field - check it's not before from (at second precision)
          if (newTimestamp < otherTimestamp) {
            return DATE_ERRORS.TO_BEFORE_FROM;
          }
        }
      }
    }

    return null;
  };

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  // Determine current target field based on click cycle
  const currentTargetField = React.useMemo(() => {
    const isOddCycle = clickCycle % 2 === 1;
    if (isOddCycle) {
      return initiatingField === "from" ? "to" : "from";
    }
    return initiatingField;
  }, [clickCycle, initiatingField]);

  // Required validation errors
  const fromRequiredError = fromRequired && !fromValue;
  const toRequiredError = toRequired && !toValue;

  // Action tooltip for calendar cells
  const actionTooltip = React.useMemo(() => {
    const target = currentTargetField === "from" ? "From" : "To";
    return `Select ${target} Date`;
  }, [currentTargetField]);

  // Resolve from/to for calendar display
  const fromDateStr = React.useMemo(() => {
    if (!fromValue) return null;
    if (isAbsoluteDate(fromValue)) {
      const date = new Date(fromValue);
      // Guard against invalid dates
      if (Number.isNaN(date.getTime())) return null;
      return formatDateToYMD(date);
    }
    const parsed = parse(fromValue, { pickerType: "single", timezone: effectiveTimezone });
    return parsed.resolved ? formatDateToYMD(parsed.resolved) : null;
  }, [fromValue, effectiveTimezone]);

  const toDateStr = React.useMemo(() => {
    if (!toValue) return null;
    if (isAbsoluteDate(toValue)) {
      const date = new Date(toValue);
      // Guard against invalid dates
      if (Number.isNaN(date.getTime())) return null;
      return formatDateToYMD(date);
    }
    const parsed = parse(toValue, { pickerType: "single", timezone: effectiveTimezone });
    return parsed.resolved ? formatDateToYMD(parsed.resolved) : null;
  }, [toValue, effectiveTimezone]);

  // Determine which field the time input should actually affect
  // - Before first date click: target the initiating field (clicked input)
  // - After first date click: toggle behavior (target last selected)
  const timeTargetField = React.useMemo((): "from" | "to" => {
    // Before first date click, target the initiating field (clicked input)
    if (clickCycle === 0) {
      return initiatingField;
    }

    // After first date click, use toggle behavior (target last selected)
    const hasFrom = !!fromDateStr;
    const hasTo = !!toDateStr;

    if (hasFrom && !hasTo) return "from";
    if (!hasFrom && hasTo) return "to";
    if (hasFrom && hasTo) {
      // Both dates exist - time affects the last selected (opposite of next to select)
      return currentTargetField === "from" ? "to" : "from";
    }
    return currentTargetField;
  }, [clickCycle, initiatingField, fromDateStr, toDateStr, currentTargetField]);

  const showPresets = presets && presets.length > 0;

  // Determine whether to show single or dual calendars
  // Single calendar when: no dates selected
  // Dual calendars when: any date is selected AND navigating away from its month
  const showDualCalendars = React.useMemo(() => {
    // If no dates selected, show single calendar
    if (!fromDateStr && !toDateStr) {
      return false;
    }

    // Check if any selected date's month is NOT visible in the left calendar
    // If so, we need dual calendars
    if (fromDateStr) {
      const [fromYear, fromMonth] = fromDateStr.split("-").map(Number);
      if (fromYear !== leftYear || fromMonth !== leftMonth) {
        return true;
      }
    }

    if (toDateStr) {
      const [toYear, toMonth] = toDateStr.split("-").map(Number);
      // If to date is in a different month than left calendar, show dual
      if (toYear !== leftYear || toMonth !== leftMonth) {
        return true;
      }
    }

    // Both dates are in the same month as left calendar (or only one date exists in that month)
    return false;
  }, [fromDateStr, toDateStr, leftYear, leftMonth]);

  // Check if from and to dates are on different calendar grids (different months)
  // When true, time inputs should be fixed (left=from, right=to)
  // When false, time inputs should follow toggle behavior
  const datesOnDifferentGrids = React.useMemo(() => {
    if (!showDualCalendars || !fromDateStr || !toDateStr) return false;
    const [fromYear, fromMonth] = fromDateStr.split("-").map(Number);
    const [toYear, toMonth] = toDateStr.split("-").map(Number);
    return fromYear !== toYear || fromMonth !== toMonth;
  }, [showDualCalendars, fromDateStr, toDateStr]);

  // Compute which calendar state shows the earlier month (for dynamic positioning)
  const calendarOrder = React.useMemo(() => {
    const leftValue = leftYear * 12 + leftMonth;
    const rightValue = rightYear * 12 + rightMonth;
    // Return which state should render on left vs right side
    if (leftValue <= rightValue) {
      return { first: "left" as const, second: "right" as const };
    }
    return { first: "right" as const, second: "left" as const };
  }, [leftYear, leftMonth, rightYear, rightMonth]);

  // Position-based navigation constraints - calendars must maintain at least 1 month gap
  // These are computed based on what's actually displayed in each position after ordering

  // First position (left side) max month - cannot navigate past one month before second position
  const firstPositionMaxMonth = React.useMemo(() => {
    if (!showDualCalendars) return undefined;
    // Get the month shown in second position
    const secondYear = calendarOrder.second === "right" ? rightYear : leftYear;
    const secondMonth = calendarOrder.second === "right" ? rightMonth : leftMonth;
    // Use 0-indexed months for calculation (subtract 1), then convert back to 1-indexed
    const secondValue = secondYear * 12 + (secondMonth - 1);
    const maxValue = secondValue - 1; // One month before second
    if (maxValue < 0) return undefined;
    return {
      year: Math.floor(maxValue / 12),
      month: (maxValue % 12) + 1, // Convert back to 1-indexed
    };
  }, [showDualCalendars, calendarOrder, leftYear, leftMonth, rightYear, rightMonth]);

  // Second position (right side) min month - cannot navigate past one month after first position
  const secondPositionMinMonth = React.useMemo(() => {
    if (!showDualCalendars) return undefined;
    // Get the month shown in first position
    const firstYear = calendarOrder.first === "left" ? leftYear : rightYear;
    const firstMonth = calendarOrder.first === "left" ? leftMonth : rightMonth;
    // Use 0-indexed months for calculation (subtract 1), then convert back to 1-indexed
    const firstValue = firstYear * 12 + (firstMonth - 1);
    const minValue = firstValue + 1; // One month after first
    return {
      year: Math.floor(minValue / 12),
      month: (minValue % 12) + 1, // Convert back to 1-indexed
    };
  }, [showDualCalendars, calendarOrder, leftYear, leftMonth, rightYear, rightMonth]);

  // ---------------------------------------------------------------------------
  // Sync State with Props
  // ---------------------------------------------------------------------------

  // Helper to normalize a date based on granularity
  // Normalize date based on granularity using CalendarDateTime
  const normalizeForGranularity = React.useCallback(
    (date: Date): Date | null => {
      const cdt = dateToCalendarDateTime(date);
      const truncated = truncateToGranularity(cdt, granularity);

      // Check if truncation changed anything
      const needsNormalization =
        cdt.hour !== truncated.hour ||
        cdt.minute !== truncated.minute ||
        cdt.second !== truncated.second ||
        cdt.millisecond !== truncated.millisecond;

      return needsNormalization ? calendarDateTimeToDate(truncated) : null;
    },
    [granularity],
  );

  // Normalize fromValue based on granularity
  React.useEffect(() => {
    if (!fromValue || !isAbsoluteDate(fromValue)) return;

    const date = new Date(fromValue);
    // Guard against invalid dates (e.g., from corrupted URL params)
    if (Number.isNaN(date.getTime())) {
      setFromValue(null);
      return;
    }
    const normalizedDate = normalizeForGranularity(date);
    if (normalizedDate) {
      setFromValue(normalizedDate.toISOString());
    }
  }, [fromValue, normalizeForGranularity, setFromValue]);

  // Normalize toValue based on granularity
  React.useEffect(() => {
    if (!toValue || !isAbsoluteDate(toValue)) return;

    const date = new Date(toValue);
    // Guard against invalid dates (e.g., from corrupted URL params)
    if (Number.isNaN(date.getTime())) {
      setToValue(null);
      return;
    }
    const normalizedDate = normalizeForGranularity(date);
    if (normalizedDate) {
      setToValue(normalizedDate.toISOString());
    }
  }, [toValue, normalizeForGranularity, setToValue]);

  React.useEffect(() => {
    // Reset manual flag — time is being derived from prop/expression, not user input
    setFromTimeManuallySet(false);
    if (!fromValue) {
      setFromInputText("");
      setFromTimeValue(getDefaultTime("from"));
      setFromErrorReason(null);
    } else if (isAbsoluteDate(fromValue)) {
      const date = new Date(fromValue);
      // Skip processing if date is invalid (will be cleared by normalization effect)
      if (Number.isNaN(date.getTime())) return;
      setFromInputText(formatForDisplay(date));
      setFromTimeValue(extractTime(date) || getDefaultTime("from"));
      setFromErrorReason(null);
    } else {
      setFromInputText(fromValue);
      const parsed = parse(fromValue, { pickerType: "single", timezone: effectiveTimezone });
      if (parsed.resolved) {
        setFromTimeValue(extractTime(parsed.resolved) || getDefaultTime("from"));
      }
      setFromErrorReason(null);
    }
  }, [fromValue, formatForDisplay, extractTime, getDefaultTime, effectiveTimezone]);

  React.useEffect(() => {
    // Reset manual flag — time is being derived from prop/expression, not user input
    setToTimeManuallySet(false);
    if (!toValue) {
      setToInputText("");
      setToTimeValue(getDefaultTime("to"));
      setToErrorReason(null);
    } else if (isAbsoluteDate(toValue)) {
      const date = new Date(toValue);
      // Skip processing if date is invalid (will be cleared by normalization effect)
      if (Number.isNaN(date.getTime())) return;
      setToInputText(formatForDisplay(date));
      setToTimeValue(extractTime(date) || getDefaultTime("to"));
      setToErrorReason(null);
    } else {
      setToInputText(toValue);
      const parsed = parse(toValue, { pickerType: "single", timezone: effectiveTimezone });
      if (parsed.resolved) {
        setToTimeValue(extractTime(parsed.resolved) || getDefaultTime("to"));
      }
      setToErrorReason(null);
    }
  }, [toValue, formatForDisplay, extractTime, getDefaultTime, effectiveTimezone]);

  // Update calendar position when values change
  React.useEffect(() => {
    if (fromValue) {
      const parsed = isAbsoluteDate(fromValue)
        ? { resolved: new Date(fromValue) }
        : parse(fromValue, { pickerType: "single", timezone: effectiveTimezone });
      if (parsed.resolved) {
        setLeftYear(parsed.resolved.getUTCFullYear());
        setLeftMonth(parsed.resolved.getUTCMonth() + 1);
      }
    }
  }, [fromValue, effectiveTimezone]);

  // Sync right calendar when switching to dual mode
  React.useEffect(() => {
    if (showDualCalendars) {
      // If to date exists, show its month in right calendar
      if (toDateStr) {
        const [toYear, toMonth] = toDateStr.split("-").map(Number);
        setRightYear(toYear);
        setRightMonth(toMonth);
      } else {
        // Default: show next month after left calendar
        const nextMonth = leftMonth === 12 ? 1 : leftMonth + 1;
        const nextYear = leftMonth === 12 ? leftYear + 1 : leftYear;
        setRightYear(nextYear);
        setRightMonth(nextMonth);
      }
    }
  }, [showDualCalendars, toDateStr, leftYear, leftMonth]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleFromClick = React.useCallback(() => {
    if (fromDisabled) return;

    // Always update initiating field and reset click cycle when clicking an input
    // This ensures time input targets the clicked field, even when popover is already open
    setInitiatingField("from");
    setClickCycle(0);

    if (!isOpen) {
      setPreviousFromValue(fromValue);
      setPreviousToValue(toValue);
      // Reset left calendar to from date's month
      if (fromValue) {
        const parsed = isAbsoluteDate(fromValue)
          ? { resolved: new Date(fromValue) }
          : parse(fromValue, { pickerType: "single", timezone: effectiveTimezone });
        if (parsed.resolved) {
          setLeftYear(parsed.resolved.getUTCFullYear());
          setLeftMonth(parsed.resolved.getUTCMonth() + 1);
        }
      }
      // Also reset right calendar to to date if it exists
      if (toValue) {
        const parsed = isAbsoluteDate(toValue)
          ? { resolved: new Date(toValue) }
          : parse(toValue, { pickerType: "single", timezone: effectiveTimezone });
        if (parsed.resolved) {
          setRightYear(parsed.resolved.getUTCFullYear());
          setRightMonth(parsed.resolved.getUTCMonth() + 1);
        }
      }
    }
    setActiveField("from");
    setIsOpen(true);
  }, [fromDisabled, isOpen, fromValue, toValue, effectiveTimezone]);

  const handleToClick = React.useCallback(() => {
    if (toDisabled) return;

    // Always update initiating field and reset click cycle when clicking an input
    // This ensures time input targets the clicked field, even when popover is already open
    setInitiatingField("to");
    setClickCycle(0);

    if (!isOpen) {
      setPreviousFromValue(fromValue);
      setPreviousToValue(toValue);
      // Reset right calendar to to date's month
      if (toValue) {
        const parsed = isAbsoluteDate(toValue)
          ? { resolved: new Date(toValue) }
          : parse(toValue, { pickerType: "single", timezone: effectiveTimezone });
        if (parsed.resolved) {
          setRightYear(parsed.resolved.getUTCFullYear());
          setRightMonth(parsed.resolved.getUTCMonth() + 1);
        }
      }
      // Also reset left calendar to from date if it exists
      if (fromValue) {
        const parsed = isAbsoluteDate(fromValue)
          ? { resolved: new Date(fromValue) }
          : parse(fromValue, { pickerType: "single", timezone: effectiveTimezone });
        if (parsed.resolved) {
          setLeftYear(parsed.resolved.getUTCFullYear());
          setLeftMonth(parsed.resolved.getUTCMonth() + 1);
        }
      }
    }
    setActiveField("to");
    setIsOpen(true);
  }, [toDisabled, isOpen, fromValue, toValue, effectiveTimezone]);

  const handleFromInputChange = React.useCallback(
    (newText: string) => {
      // Check for range expression like "now-7d to now" using parser's range detector
      const rangeResult = detectRange(newText);
      if (rangeResult.isRange && rangeResult.from && rangeResult.to) {
        const fromParsed = parse(rangeResult.from, {
          pickerType: "single",
          timezone: effectiveTimezone,
          fieldType: "from",
        });
        const toParsed = parse(rangeResult.to, { pickerType: "single", timezone: effectiveTimezone, fieldType: "to" });

        if (fromParsed.isValid && fromParsed.resolved && toParsed.isValid && toParsed.resolved) {
          // Both parts valid - update both fields
          setFromErrorReason(null);
          setToErrorReason(null);

          // When immediatelyResolveExpressions is true, always use ISO strings
          const fromResult =
            fromParsed.isExpression && !immediatelyResolveExpressions
              ? fromParsed.normalized
              : fromParsed.resolved.toISOString();
          const toResult =
            toParsed.isExpression && !immediatelyResolveExpressions
              ? toParsed.normalized
              : toParsed.resolved.toISOString();

          // Always update TO input text (safe - different input than the one being blurred)
          // When immediatelyResolveExpressions, show formatted date instead of expression
          setToInputText(
            immediatelyResolveExpressions ? formatForDisplay(toParsed.resolved) : rangeResult.to,
          );

          // Only update FROM input text if value won't change (to avoid double-blur)
          // If value changes, the useEffect will sync it
          if (fromResult === fromValue) {
            setFromInputText(
              immediatelyResolveExpressions ? formatForDisplay(fromParsed.resolved) : rangeResult.from,
            );
          }

          setFromValue(fromResult);
          setToValue(toResult);

          setLeftYear(fromParsed.resolved.getUTCFullYear());
          setLeftMonth(fromParsed.resolved.getUTCMonth() + 1);
          return;
        }
      }

      setFromInputText(newText);

      // Check if input references start/end fields
      const lowerText = newText.toLowerCase().trim();
      const referencesStart = lowerText.startsWith("start");
      const referencesEnd = lowerText.startsWith("end");

      // Cannot use "start" in the start field - it would be self-referential
      if (referencesStart) {
        setFromErrorReason('"start" can only be used in the end date field');
        return;
      }

      if (referencesEnd && !toValue) {
        setFromErrorReason('Enter a valid date in the end field first');
        return;
      }

      // Check for circular reference - end field references start while we reference end
      if (referencesEnd && toValue && toValue.toLowerCase().trim().startsWith("start")) {
        setFromErrorReason('Circular reference: end field already references start');
        return;
      }

      // Get base date for end references
      let baseDate: Date | undefined;
      if (referencesEnd && toValue) {
        const toParsed = parse(toValue, { pickerType: "single", timezone: effectiveTimezone });
        baseDate = toParsed.resolved ?? undefined;
      }

      const parsed = parse(newText, { pickerType: "single", timezone: effectiveTimezone, fieldType: "from", baseDate });

      if (parsed.isValid && parsed.resolved) {
        const resolvedDateStr = formatDateToYMD(parsed.resolved);
        const error = getDateRangeError(resolvedDateStr, parsed.resolved, "from");

        if (error) {
          setFromErrorReason(error);
          return;
        }

        setFromErrorReason(null);
        // Clear related range error on the other field if it exists
        setToErrorReason((prev) => {
          if (prev === DATE_ERRORS.TO_BEFORE_FROM) return null;
          if (prev === "Enter a valid date in the start field first") return null;
          if (prev === "Circular reference: start field already references end") return null;
          return prev;
        });

        // When immediatelyResolveExpressions is true, always use ISO strings
        if (parsed.isExpression && !immediatelyResolveExpressions) {
          setFromValue(parsed.normalized);
        } else {
          setFromValue(parsed.resolved.toISOString());
        }

        // When immediatelyResolveExpressions, update input to show resolved date
        if (parsed.isExpression && immediatelyResolveExpressions) {
          setFromInputText(formatForDisplay(parsed.resolved));
        }

        setLeftYear(parsed.resolved.getUTCFullYear());
        setLeftMonth(parsed.resolved.getUTCMonth() + 1);
      } else if (newText === "") {
        setFromErrorReason(null);
        // Clear related range error on the other field if it exists
        setToErrorReason((prev) => {
          if (prev === DATE_ERRORS.TO_BEFORE_FROM) return null;
          if (prev === "Enter a valid date in the start field first") return null;
          if (prev === "Circular reference: start field already references end") return null;
          return prev;
        });
        setFromValue(null);
      } else {
        // Use specific error from parser if available, otherwise generic message
        setFromErrorReason(parsed.error || DATE_ERRORS.INVALID_FORMAT);
      }
    },
    [effectiveTimezone, setFromValue, setToValue, toValue, resolvedMinDate, resolvedMaxDate, immediatelyResolveExpressions, formatForDisplay],
  );

  const handleToInputChange = React.useCallback(
    (newText: string) => {
      // Check for range expression like "now-7d to now" using parser's range detector
      const rangeResult = detectRange(newText);
      if (rangeResult.isRange && rangeResult.from && rangeResult.to) {
        const fromParsed = parse(rangeResult.from, {
          pickerType: "single",
          timezone: effectiveTimezone,
          fieldType: "from",
        });
        const toParsed = parse(rangeResult.to, { pickerType: "single", timezone: effectiveTimezone, fieldType: "to" });

        if (fromParsed.isValid && fromParsed.resolved && toParsed.isValid && toParsed.resolved) {
          // Both parts valid - update both fields
          setFromErrorReason(null);
          setToErrorReason(null);

          // When immediatelyResolveExpressions is true, always use ISO strings
          const fromResult =
            fromParsed.isExpression && !immediatelyResolveExpressions
              ? fromParsed.normalized
              : fromParsed.resolved.toISOString();
          const toResult =
            toParsed.isExpression && !immediatelyResolveExpressions
              ? toParsed.normalized
              : toParsed.resolved.toISOString();

          // Always update FROM input text (safe - different input than the one being blurred)
          // When immediatelyResolveExpressions, show formatted date instead of expression
          setFromInputText(
            immediatelyResolveExpressions ? formatForDisplay(fromParsed.resolved) : rangeResult.from,
          );

          // Only update TO input text if value won't change (to avoid double-blur)
          // If value changes, the useEffect will sync it
          if (toResult === toValue) {
            setToInputText(
              immediatelyResolveExpressions ? formatForDisplay(toParsed.resolved) : rangeResult.to,
            );
          }

          setFromValue(fromResult);
          setToValue(toResult);

          setLeftYear(fromParsed.resolved.getUTCFullYear());
          setLeftMonth(fromParsed.resolved.getUTCMonth() + 1);
          return;
        }
      }

      setToInputText(newText);

      // Check if input references start/end fields
      const lowerText = newText.toLowerCase().trim();
      const referencesStart = lowerText.startsWith("start");
      const referencesEnd = lowerText.startsWith("end");

      // Cannot use "end" in the end field - it would be self-referential
      if (referencesEnd) {
        setToErrorReason('"end" can only be used in the start date field');
        return;
      }

      if (referencesStart && !fromValue) {
        setToErrorReason('Enter a valid date in the start field first');
        return;
      }

      // Check for circular reference - start field references end while we reference start
      if (referencesStart && fromValue && fromValue.toLowerCase().trim().startsWith("end")) {
        setToErrorReason('Circular reference: start field already references end');
        return;
      }

      // Get base date for start references
      let baseDate: Date | undefined;
      if (referencesStart && fromValue) {
        const fromParsed = parse(fromValue, { pickerType: "single", timezone: effectiveTimezone });
        baseDate = fromParsed.resolved ?? undefined;
      }

      const parsed = parse(newText, { pickerType: "single", timezone: effectiveTimezone, fieldType: "to", baseDate });

      if (parsed.isValid && parsed.resolved) {
        const resolvedDateStr = formatDateToYMD(parsed.resolved);
        const error = getDateRangeError(resolvedDateStr, parsed.resolved, "to");

        if (error) {
          setToErrorReason(error);
          return;
        }

        setToErrorReason(null);
        // Clear related range error on the other field if it exists
        setFromErrorReason((prev) => {
          if (prev === DATE_ERRORS.FROM_AFTER_TO) return null;
          if (prev === "Enter a valid date in the end field first") return null;
          if (prev === "Circular reference: end field already references start") return null;
          return prev;
        });

        // When immediatelyResolveExpressions is true, always use ISO strings
        if (parsed.isExpression && !immediatelyResolveExpressions) {
          setToValue(parsed.normalized);
        } else {
          setToValue(parsed.resolved.toISOString());
        }

        // When immediatelyResolveExpressions, update input to show resolved date
        if (parsed.isExpression && immediatelyResolveExpressions) {
          setToInputText(formatForDisplay(parsed.resolved));
        }
      } else if (newText === "") {
        setToErrorReason(null);
        // Clear related range error on the other field if it exists
        setFromErrorReason((prev) => {
          if (prev === DATE_ERRORS.FROM_AFTER_TO) return null;
          if (prev === "Enter a valid date in the end field first") return null;
          if (prev === "Circular reference: end field already references start") return null;
          return prev;
        });
        setToValue(null);
      } else {
        // Use specific error from parser if available, otherwise generic message
        setToErrorReason(parsed.error || DATE_ERRORS.INVALID_FORMAT);
      }
    },
    [effectiveTimezone, setFromValue, setToValue, fromValue, resolvedMinDate, resolvedMaxDate, immediatelyResolveExpressions, formatForDisplay],
  );

  const handleFromClear = React.useCallback(() => {
    setFromInputText("");
    setFromErrorReason(null);
    setFromValue(null);
  }, [setFromValue]);

  const handleToClear = React.useCallback(() => {
    setToInputText("");
    setToErrorReason(null);
    setToValue(null);
  }, [setToValue]);

  const handleFromUndo = React.useCallback(() => {
    if (previousFromValue !== undefined) {
      if (previousFromValue === null) {
        setFromInputText("");
        setFromValue(null);
      } else if (isAbsoluteDate(previousFromValue)) {
        setFromInputText(formatForDisplay(new Date(previousFromValue)));
        setFromValue(previousFromValue);
      } else {
        setFromInputText(previousFromValue);
        setFromValue(previousFromValue);
      }
      setFromErrorReason(null);
    }
  }, [previousFromValue, setFromValue]);

  const handleToUndo = React.useCallback(() => {
    if (previousToValue !== undefined) {
      if (previousToValue === null) {
        setToInputText("");
        setToValue(null);
      } else if (isAbsoluteDate(previousToValue)) {
        setToInputText(formatForDisplay(new Date(previousToValue)));
        setToValue(previousToValue);
      } else {
        setToInputText(previousToValue);
        setToValue(previousToValue);
      }
      setToErrorReason(null);
    }
  }, [previousToValue, setToValue]);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    setHoveredDate(null);
  }, []);

  const handleDateClick = React.useCallback(
    (dateStr: string) => {
      // Validate against min/max
      const isBeforeMin = resolvedMinDate && dateStr < resolvedMinDate;
      const isAfterMax = resolvedMaxDate && dateStr > resolvedMaxDate;
      if (isBeforeMin || isAfterMax) return;

      const targetField = currentTargetField;

      // Get effective time value, using current time state or default
      // Only preserves existing time if the user manually set it (not derived from expression resolution)
      const getEffectiveTime = (fieldType: FieldType): string => {
        const timeVal = fieldType === "from" ? fromTimeValue : toTimeValue;
        const isManual = fieldType === "from" ? fromTimeManuallySet : toTimeManuallySet;

        // Only use existing time if the user explicitly set it via the time input
        if (timeVal && isManual) {
          const timeGranularity = granularity === "date" ? "minute" : granularity;
          const parsed = parseTimeInput(timeVal, timeGranularity);
          if (parsed.isValid) {
            return parsed.value;
          }
        }

        return getDefaultTime(fieldType);
      };

      if (targetField === "from") {
        // Setting from date
        const newFromISO = buildISOString(dateStr, getEffectiveTime("from"));

        // If to date exists and new from is after to, clear to and keep selecting to
        if (toDateStr && dateStr > toDateStr) {
          setToValue(null);
          setToInputText("");
          setToErrorReason(null);
          setFromValue(newFromISO);
          setFromErrorReason(null);
          // Reset state to continue selecting "to"
          setInitiatingField("to");
          setClickCycle(0);
          setActiveField("to");
          setHoveredDate(null);
          return;
        }

        setFromValue(newFromISO);
        setFromErrorReason(null);
      } else {
        // Setting to date
        const newToISO = buildISOString(dateStr, getEffectiveTime("to"));

        // If from date exists and new to is before from, set as new from, clear to, keep selecting to
        if (fromDateStr && dateStr < fromDateStr) {
          setFromValue(buildISOString(dateStr, getEffectiveTime("from")));
          setFromErrorReason(null);
          setToValue(null);
          setToInputText("");
          setToErrorReason(null);
          // Reset state to continue selecting "to"
          setInitiatingField("to");
          setClickCycle(0);
          setActiveField("to");
          setHoveredDate(null);
          return;
        }

        setToValue(newToISO);
        setToErrorReason(null);
      }

      // Increment click cycle and update active field
      setClickCycle((prev) => prev + 1);
      setActiveField(targetField === "from" ? "to" : "from");
      setHoveredDate(null);
      // NO auto-close - user clicks outside to close
    },
    [
      currentTargetField,
      fromDateStr,
      toDateStr,
      setFromValue,
      setToValue,
      resolvedMinDate,
      resolvedMaxDate,
      buildISOString,
      getDefaultTime,
      fromTimeValue,
      toTimeValue,
      fromTimeManuallySet,
      toTimeManuallySet,
      granularity,
    ],
  );

  const handleDateHover = React.useCallback((date: string | null) => {
    setHoveredDate(date);
  }, []);

  const handleLeftMonthChange = React.useCallback((year: number, month: number) => {
    setLeftYear(year);
    setLeftMonth(month);
  }, []);

  const handleRightMonthChange = React.useCallback((year: number, month: number) => {
    setRightYear(year);
    setRightMonth(month);
  }, []);

  const handlePresetClick = React.useCallback(
    (preset: RangePreset) => {
      const [fromValue, toValue] = preset.value;

      const fromParsed = parse(fromValue, { pickerType: "single", timezone: effectiveTimezone, fieldType: "from" });
      const toParsed = parse(toValue, { pickerType: "single", timezone: effectiveTimezone, fieldType: "to" });

      // From must be valid and resolved
      // To can be empty (which means "now" / open-ended)
      const toIsEmpty = !toValue.trim();
      const toIsValidResolved = toParsed.isValid && toParsed.resolved;

      if (fromParsed.isValid && fromParsed.resolved && (toIsEmpty || toIsValidResolved)) {
        // Validate against min/max (only if to has a resolved value)
        const fromDateStr = formatDateToYMD(fromParsed.resolved);
        const fromBeforeMin = resolvedMinDate && fromDateStr < resolvedMinDate;

        if (fromBeforeMin) {
          return; // Don't apply preset if out of range
        }

        if (toIsValidResolved) {
          const toDateStr = formatDateToYMD(toParsed.resolved!);
          const toAfterMax = resolvedMaxDate && toDateStr > resolvedMaxDate;
          if (toAfterMax) {
            return; // Don't apply preset if out of range
          }
        }

        setFromErrorReason(null);
        setToErrorReason(null);

        // Preserve expressions unless immediatelyResolveExpressions is enabled
        if (fromParsed.isExpression && !immediatelyResolveExpressions) {
          setFromValue(fromParsed.normalized);
        } else {
          setFromValue(fromParsed.resolved.toISOString());
        }

        if (toIsEmpty) {
          // Empty to value means clear it (open-ended / "now")
          setToValue(null);
        } else if (toParsed.isExpression && !immediatelyResolveExpressions) {
          setToValue(toParsed.normalized);
        } else {
          setToValue(toParsed.resolved!.toISOString());
        }

        // Update calendar to show from date
        setLeftYear(fromParsed.resolved.getUTCFullYear());
        setLeftMonth(fromParsed.resolved.getUTCMonth() + 1);

        setIsOpen(false);
      }
    },
    [effectiveTimezone, setFromValue, setToValue, resolvedMinDate, resolvedMaxDate, immediatelyResolveExpressions],
  );

  const handleTimezoneChange = React.useCallback(
    (newTimezone: string) => {
      // When "updateDisplay" is set, convert values so the same instant is shown
      if (timezoneSwitchBehavior === "updateDisplay") {
        const convertedFrom = convertValueForTimezoneSwitch(fromValue, effectiveTimezone, newTimezone);
        const convertedTo = convertValueForTimezoneSwitch(toValue, effectiveTimezone, newTimezone);

        if (convertedFrom !== fromValue) {
          setFromValue(convertedFrom);
        }
        if (convertedTo !== toValue) {
          setToValue(convertedTo);
        }
      }
      // "keepDisplay" (default): values stay the same, only interpretation changes

      onTimezoneChange?.(newTimezone);
    },
    [onTimezoneChange, timezoneSwitchBehavior, fromValue, toValue, effectiveTimezone, setFromValue, setToValue],
  );

  // Count expressions for button label and visibility
  const expressionCount = React.useMemo(() => countExpressions(fromValue, toValue), [fromValue, toValue]);
  const hasExpressions = expressionCount > 0;

  // Handler to convert expressions to fixed ISO dates
  const handleApplyExpressions = React.useCallback(() => {
    applyExpression(fromValue, effectiveTimezone, setFromValue);
    applyExpression(toValue, effectiveTimezone, setToValue);
  }, [fromValue, toValue, effectiveTimezone, setFromValue, setToValue]);

  // Handler to force re-render on hover for dynamic tooltip evaluation
  const handleMouseEnter = React.useCallback(() => {
    // Only trigger re-render if any value is an expression (not absolute date)
    if (hasExpressions) {
      setHoverTick((t) => t + 1);
    }
  }, [hasExpressions]);

  // Time input handlers for granularity support
  // Uses timeTargetField which accounts for which date actually exists
  const handleTimeChange = React.useCallback(
    (time: string) => {
      if (timeTargetField === "from") {
        setFromTimeValue(time);
        setFromTimeManuallySet(true);
      } else {
        setToTimeValue(time);
        setToTimeManuallySet(true);
      }
    },
    [timeTargetField],
  );

  const handleTimeBlur = React.useCallback(
    (time: string, isValid: boolean) => {
      // Update error state based on validity
      if (timeTargetField === "from") {
        setFromTimeError(!isValid);
        // Save previous valid value for undo
        if (isValid && time) {
          setPreviousFromTimeValue(fromTimeValue);
        }
      } else {
        setToTimeError(!isValid);
        // Save previous valid value for undo
        if (isValid && time) {
          setPreviousToTimeValue(toTimeValue);
        }
      }

      // Only update ISO if valid and date already set
      if (!isValid) return;

      if (timeTargetField === "from" && fromDateStr) {
        setFromValue(buildISOString(fromDateStr, time || getDefaultTime("from")));
      } else if (timeTargetField === "to" && toDateStr) {
        setToValue(buildISOString(toDateStr, time || getDefaultTime("to")));
      }
    },
    [timeTargetField, fromDateStr, toDateStr, setFromValue, setToValue, buildISOString, getDefaultTime, fromTimeValue, toTimeValue],
  );

  // Fixed time handlers (used when dates are on different grids)
  const handleFromTimeChange = React.useCallback((time: string) => {
    setFromTimeValue(time);
    setFromTimeManuallySet(true);
  }, []);

  const handleFromTimeBlur = React.useCallback(
    (time: string, isValid: boolean) => {
      setFromTimeError(!isValid);
      // Save previous valid value for undo
      if (isValid && time) {
        setPreviousFromTimeValue(fromTimeValue);
      }
      if (!isValid) return;

      if (fromDateStr) {
        setFromValue(buildISOString(fromDateStr, time || getDefaultTime("from")));
      }
    },
    [fromDateStr, setFromValue, buildISOString, getDefaultTime, fromTimeValue],
  );

  const handleToTimeChange = React.useCallback((time: string) => {
    setToTimeValue(time);
    setToTimeManuallySet(true);
  }, []);

  const handleToTimeBlur = React.useCallback(
    (time: string, isValid: boolean) => {
      setToTimeError(!isValid);
      // Save previous valid value for undo
      if (isValid && time) {
        setPreviousToTimeValue(toTimeValue);
      }
      if (!isValid) return;

      if (toDateStr) {
        setToValue(buildISOString(toDateStr, time || getDefaultTime("to")));
      }
    },
    [toDateStr, setToValue, buildISOString, getDefaultTime, toTimeValue],
  );

  // Time undo handlers
  const handleTimeUndo = React.useCallback(() => {
    if (timeTargetField === "from" && previousFromTimeValue !== null) {
      setFromTimeValue(previousFromTimeValue);
      setFromTimeError(false);
      if (fromDateStr) {
        setFromValue(buildISOString(fromDateStr, previousFromTimeValue || getDefaultTime("from")));
      }
    } else if (timeTargetField === "to" && previousToTimeValue !== null) {
      setToTimeValue(previousToTimeValue);
      setToTimeError(false);
      if (toDateStr) {
        setToValue(buildISOString(toDateStr, previousToTimeValue || getDefaultTime("to")));
      }
    }
  }, [timeTargetField, previousFromTimeValue, previousToTimeValue, fromDateStr, toDateStr, setFromValue, setToValue, buildISOString, getDefaultTime]);

  const handleFromTimeUndo = React.useCallback(() => {
    if (previousFromTimeValue !== null) {
      setFromTimeValue(previousFromTimeValue);
      setFromTimeError(false);
      if (fromDateStr) {
        setFromValue(buildISOString(fromDateStr, previousFromTimeValue || getDefaultTime("from")));
      }
    }
  }, [previousFromTimeValue, fromDateStr, setFromValue, buildISOString, getDefaultTime]);

  const handleToTimeUndo = React.useCallback(() => {
    if (previousToTimeValue !== null) {
      setToTimeValue(previousToTimeValue);
      setToTimeError(false);
      if (toDateStr) {
        setToValue(buildISOString(toDateStr, previousToTimeValue || getDefaultTime("to")));
      }
    }
  }, [previousToTimeValue, toDateStr, setToValue, buildISOString, getDefaultTime]);

  // ---------------------------------------------------------------------------
  // Tooltips
  // ---------------------------------------------------------------------------

  // Unified tooltip - memoize static parts, evaluate expressions fresh
  const staticTooltip = React.useMemo(() => {
    if (isOpen) return { type: "hidden" as const };

    // Required errors first
    if (fromRequiredError && toRequiredError) return { type: "error" as const, message: "Both dates are required" };
    if (fromRequiredError) return { type: "error" as const, message: "From date is required" };
    if (toRequiredError) return { type: "error" as const, message: "To date is required" };

    // Parse errors next
    if (fromErrorReason || toErrorReason) {
      const errors: string[] = [];
      if (fromErrorReason) errors.push(`From: ${fromErrorReason}`);
      if (toErrorReason) errors.push(`To: ${toErrorReason}`);
      return { type: "error" as const, message: errors.join("\n") };
    }

    // Check if we need dynamic evaluation
    const fromIsExpression = fromValue && !isAbsoluteDate(fromValue);
    const toIsExpression = toValue && !isAbsoluteDate(toValue);

    if (fromIsExpression || toIsExpression) {
      return { type: "dynamic" as const };
    }

    // Both are absolute dates or empty - memoize
    const fromDate = fromValue ? new Date(fromValue) : null;
    const toDate = toValue ? new Date(toValue) : null;

    if (fromDate && toDate) {
      return {
        type: "value" as const,
        message: `From:\n${formatTooltipWithTimezone(fromDate, effectiveTimezone)}\n\nTo:\n${formatTooltipWithTimezone(
          toDate,
          effectiveTimezone,
        )}`,
      };
    }
    if (fromDate)
      return { type: "value" as const, message: `Since:\n${formatTooltipWithTimezone(fromDate, effectiveTimezone)}` };
    if (toDate)
      return { type: "value" as const, message: `Until:\n${formatTooltipWithTimezone(toDate, effectiveTimezone)}` };

    return { type: "hint" as const, message: "Try: 2024-01-15, now-7d, yesterday, last week" };
  }, [
    isOpen,
    fromValue,
    toValue,
    fromRequiredError,
    toRequiredError,
    fromErrorReason,
    toErrorReason,
    effectiveTimezone,
  ]);

  // Compute final tooltip - evaluate expressions fresh each render
  const unifiedTooltip = (() => {
    if (staticTooltip.type === "hidden") return undefined;
    if (staticTooltip.type === "error" || staticTooltip.type === "value" || staticTooltip.type === "hint") {
      return staticTooltip.message;
    }
    // Dynamic - resolve expressions fresh with forDisplay: true for proper timezone handling
    // Handle start/end cross-references by resolving in the correct order
    const fromLower = (fromValue ?? "").toLowerCase().trim();
    const toLower = (toValue ?? "").toLowerCase().trim();
    const fromReferencesEnd = fromLower.startsWith("end");
    const toReferencesStart = toLower.startsWith("start");

    let fromDate: Date | null = null;
    let toDate: Date | null = null;

    if (fromReferencesEnd && toValue) {
      // Resolve toValue first, then use it as baseDate for fromValue
      toDate = resolveExpression(toValue, effectiveTimezone, { forDisplay: true });
      if (toDate) {
        const fromParsed = parse(fromValue ?? "", { pickerType: "single", timezone: effectiveTimezone, baseDate: toDate });
        fromDate = fromParsed.resolved ?? null;
      }
    } else if (toReferencesStart && fromValue) {
      // Resolve fromValue first, then use it as baseDate for toValue
      fromDate = resolveExpression(fromValue, effectiveTimezone, { forDisplay: true });
      if (fromDate) {
        const toParsed = parse(toValue ?? "", { pickerType: "single", timezone: effectiveTimezone, baseDate: fromDate });
        toDate = toParsed.resolved ?? null;
      }
    } else {
      // No cross-references, resolve normally
      fromDate = resolveExpression(fromValue ?? "", effectiveTimezone, { forDisplay: true });
      toDate = resolveExpression(toValue ?? "", effectiveTimezone, { forDisplay: true });
    }

    if (fromDate && toDate) {
      return `From:\n${formatTooltipWithTimezone(fromDate, effectiveTimezone)}\n\nTo:\n${formatTooltipWithTimezone(
        toDate,
        effectiveTimezone,
      )}`;
    }
    if (fromDate) return `Since:\n${formatTooltipWithTimezone(fromDate, effectiveTimezone)}`;
    if (toDate) return `Until:\n${formatTooltipWithTimezone(toDate, effectiveTimezone)}`;

    return "Try: 2024-01-15, now-7d, yesterday, last week";
  })();

  // ---------------------------------------------------------------------------
  // Computed Classes
  // ---------------------------------------------------------------------------

  const hasError = !!fromErrorReason || !!toErrorReason || fromRequiredError || toRequiredError;
  const isDisabled = fromDisabled && toDisabled;

  const inputsClasses = [
    "date-range-picker__inputs",
    hasError && "date-range-picker__inputs--error",
    isDisabled && "date-range-picker__inputs--disabled",
  ]
    .filter(Boolean)
    .join(" ");

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Build class name with granularity variant
  const pickerClassName = ["date-range-picker", `date-range-picker--${granularity}`, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={anchorRef} className={pickerClassName} style={style}>
      <div className={inputsClasses} onMouseEnter={handleMouseEnter}>
        <DateInput
          value={fromInputText}
          onChange={handleFromInputChange}
          isError={!!fromErrorReason || fromRequiredError}
          previousValue={
            previousFromValue === undefined
              ? null
              : previousFromValue === null
              ? ""
              : isAbsoluteDate(previousFromValue)
              ? formatForDisplay(new Date(previousFromValue))
              : previousFromValue
          }
          onUndo={handleFromUndo}
          allowClear={fromAllowClear}
          onClear={handleFromClear}
          disabled={fromDisabled}
          placeholder={resolvedFromPlaceholder}
          onClick={handleFromClick}
          className={activeField === "from" && isOpen ? "date-input--active" : ""}
          aria-label="From date"
        />
        <span className="date-range-picker__separator">→</span>
        <DateInput
          value={toInputText}
          onChange={handleToInputChange}
          isError={!!toErrorReason || toRequiredError}
          previousValue={
            previousToValue === undefined
              ? null
              : previousToValue === null
              ? ""
              : isAbsoluteDate(previousToValue)
              ? formatForDisplay(new Date(previousToValue))
              : previousToValue
          }
          onUndo={handleToUndo}
          allowClear={toAllowClear}
          onClear={handleToClear}
          disabled={toDisabled}
          placeholder={resolvedToPlaceholder}
          onClick={handleToClick}
          className={activeField === "to" && isOpen ? "date-input--active" : ""}
          aria-label="To date"
        />

        {/* Custom Tooltip */}
        {!isOpen && <PickerTooltip content={unifiedTooltip} position={tooltipPosition} className="date-range-picker__tooltip" />}
      </div>

      <Popover open={isOpen} onClose={handleClose} anchorEl={anchorRef.current}>
        <div className={`date-range-picker__popover${showPresets ? " date-range-picker__popover--with-presets" : ""}`}>
          {/* Presets */}
          {showPresets && (
            <div className="date-range-picker__presets">
              <PresetList
                presets={presets}
                activeValue={fromValue && toValue ? [fromInputText, toInputText] : undefined}
                onPresetClick={handlePresetClick}
              />
            </div>
          )}

          {/* Calendars and Timezone Container */}
          <div className="date-range-picker__main">
            {/* Calendars - single or dual based on selection state */}
            {/* Dynamic positioning: earlier month always on left, later on right */}
            <div className="date-range-picker__calendars">
              {/* First calendar (left position) - earlier month */}
              <CalendarBox
                year={calendarOrder.first === "left" ? leftYear : rightYear}
                month={calendarOrder.first === "left" ? leftMonth : rightMonth}
                onMonthChange={calendarOrder.first === "left" ? handleLeftMonthChange : handleRightMonthChange}
                rangeStart={fromDateStr}
                rangeEnd={toDateStr}
                hoveredDate={hoveredDate}
                editingField={currentTargetField}
                minDate={resolvedMinDate}
                maxDate={resolvedMaxDate}
                maxMonth={firstPositionMaxMonth}
                onDateClick={handleDateClick}
                onDateHover={handleDateHover}
                timezone={effectiveTimezone}
                showTime={
                  granularity !== "date" &&
                  (datesOnDifferentGrids
                    ? !fromValue || isAbsoluteDate(fromValue) // For left calendar: hide if from is expression
                    : timeTargetField === "from"
                    ? !fromValue || isAbsoluteDate(fromValue)
                    : !toValue || isAbsoluteDate(toValue))
                }
                timeValue={
                  datesOnDifferentGrids ? fromTimeValue : timeTargetField === "from" ? fromTimeValue : toTimeValue
                }
                onTimeChange={datesOnDifferentGrids ? handleFromTimeChange : handleTimeChange}
                onTimeBlur={datesOnDifferentGrids ? handleFromTimeBlur : handleTimeBlur}
                isTimeError={datesOnDifferentGrids ? fromTimeError : timeTargetField === "from" ? fromTimeError : toTimeError}
                previousTimeValue={
                  datesOnDifferentGrids ? previousFromTimeValue : timeTargetField === "from" ? previousFromTimeValue : previousToTimeValue
                }
                onTimeUndo={datesOnDifferentGrids ? handleFromTimeUndo : handleTimeUndo}
                timeGranularity={granularity === "date" ? "minute" : granularity}
                timeLabel={datesOnDifferentGrids ? "From time" : timeTargetField === "from" ? "From time" : "To time"}
                actionTooltip={actionTooltip}
                showOutsideDays={!showDualCalendars}
                className={showDualCalendars ? "date-range-picker__calendar-left" : undefined}
              />
              {/* Second calendar (right position) - later month */}
              {showDualCalendars && (
                <CalendarBox
                  year={calendarOrder.second === "right" ? rightYear : leftYear}
                  month={calendarOrder.second === "right" ? rightMonth : leftMonth}
                  onMonthChange={calendarOrder.second === "right" ? handleRightMonthChange : handleLeftMonthChange}
                  rangeStart={fromDateStr}
                  rangeEnd={toDateStr}
                  hoveredDate={hoveredDate}
                  editingField={currentTargetField}
                  minDate={resolvedMinDate}
                  maxDate={resolvedMaxDate}
                  minMonth={secondPositionMinMonth}
                  onDateClick={handleDateClick}
                  onDateHover={handleDateHover}
                  timezone={effectiveTimezone}
                  showTime={
                    granularity !== "date" &&
                    (datesOnDifferentGrids
                      ? !toValue || isAbsoluteDate(toValue) // For right calendar: hide if to is expression
                      : timeTargetField === "from"
                      ? !fromValue || isAbsoluteDate(fromValue)
                      : !toValue || isAbsoluteDate(toValue))
                  }
                  timeValue={
                    datesOnDifferentGrids ? toTimeValue : timeTargetField === "from" ? fromTimeValue : toTimeValue
                  }
                  onTimeChange={datesOnDifferentGrids ? handleToTimeChange : handleTimeChange}
                  onTimeBlur={datesOnDifferentGrids ? handleToTimeBlur : handleTimeBlur}
                  isTimeError={datesOnDifferentGrids ? toTimeError : timeTargetField === "from" ? fromTimeError : toTimeError}
                  previousTimeValue={
                    datesOnDifferentGrids ? previousToTimeValue : timeTargetField === "from" ? previousFromTimeValue : previousToTimeValue
                  }
                  onTimeUndo={datesOnDifferentGrids ? handleToTimeUndo : handleTimeUndo}
                  timeGranularity={granularity === "date" ? "minute" : granularity}
                  timeLabel={datesOnDifferentGrids ? "To time" : timeTargetField === "from" ? "From time" : "To time"}
                  actionTooltip={actionTooltip}
                  showOutsideDays={!showDualCalendars}
                  className="date-range-picker__calendar-right"
                />
              )}
            </div>

            {/* Footer - Timezone Selector above Apply button, aligned right */}
            {(hasExpressions || onTimezoneChange) && (
              <div className="date-range-picker__footer">
                {onTimezoneChange && (
                  <TimezoneSelector
                    value={effectiveTimezone}
                    onChange={handleTimezoneChange}
                    localTimezone={LOCAL_TIMEZONE}
                  />
                )}
                {hasExpressions && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleApplyExpressions}
                    title="Convert relative expressions to fixed dates for shareable URL"
                  >
                    {expressionCount > 1 ? "Apply expressions" : "Apply expression"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Popover>
    </div>
  );
});
